import { LogType } from '../types/visualizer';
import { InfoBar } from './info-bar';
import { GetContactResultResponse } from '../../api/api-client';
import { LogNavigationProvider } from '../contexts/log-navigation-context';
import React from 'react';
import { ReactFlow, Controls, Background, type Node, type Edge, MiniMap, ControlButton } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useLocalStorage } from '@@/hooks/use-local-storage';
import { FlowNode } from './flow-canvas/nodes/flow-node';
import { ActionNode } from './flow-canvas/nodes/action-node';
import { StartNode } from './flow-canvas/nodes/start-node';
import { EndNode } from './flow-canvas/nodes/end-node';
import { FlowBlockNode } from './flow-canvas/nodes/flow-block-node';
import { FlowGroupNode } from './flow-canvas/nodes/flow-group-node';
import { OutcomeEdge } from './flow-canvas/edges/outcome-edge';
import { MapPinIcon, MapPinOffIcon } from 'lucide-react';
import { ProcessorFactory } from '../services/processor-factory';

export const FlowCanvas = (props: { contactId: string; contactResult: GetContactResultResponse }) => {
  const [showMinimap, setShowMinimap] = useLocalStorage<boolean>('cv-show-mini-map', true);

  let nodeData: {
    nodes: Node[];
    edges: Edge[];
  } = {
    nodes: [],
    edges: []
  };

  const processor = ProcessorFactory.getProcessor(LogType.INTERACTION_LOG);
  nodeData = processor.processAndConvert(props.contactResult.interactionLogsObject.InteractionLogs);

  return (
    <ReactFlow
      defaultNodes={nodeData.nodes}
      defaultEdges={nodeData.edges}
      nodeTypes={{
        flow: FlowNode,
        action: ActionNode,
        start: StartNode,
        end: EndNode,
        flowBlock: FlowBlockNode,
        flowGroup: FlowGroupNode
      }}
      edgeTypes={{
        outcome: OutcomeEdge
      }}
      nodesDraggable={true}
      nodesConnectable={false}
      elementsSelectable={true}
      zoomOnScroll={true}
      zoomOnPinch={true}
      zoomOnDoubleClick={true}
      panOnDrag={true}
      panOnScroll={false}
      defaultEdgeOptions={{
        zIndex: 1000
      }}
    >
      <Background gap={16} />
      <Controls showInteractive={false}>
        <ControlButton
          onClick={() => setShowMinimap(!showMinimap)}
          title={showMinimap ? 'Hide Minimap' : 'Show Minimap'}
        >
          {showMinimap ? <MapPinIcon /> : <MapPinOffIcon />}
        </ControlButton>
      </Controls>
      {showMinimap && (
        <MiniMap
          pannable={true}
          zoomable={true}
          className='bg-neutral-50 border border-neutral-200 rounded-lg'
          nodeColor='rgb(178, 178, 178)'
        />
      )}
    </ReactFlow>
  );
};

export function LocalFlowVisualization(props: { contactResult: GetContactResultResponse; onClose: () => void }) {
  const allowedLogTypes = [LogType.INTERACTION_LOG];

  const timestamp = props.contactResult.contact.InitiationTimestamp
    ? new Date(props.contactResult.contact.InitiationTimestamp).toISOString()
    : new Date().toISOString();

  return (
    <LogNavigationProvider>
      <div className='flex-1 flex flex-col h-full relative'>
        <InfoBar
          allowedLogTypes={allowedLogTypes}
          initiationTimestamp={timestamp}
          contactId={props.contactResult.contact.Id as string}
          relatedContactId={props.contactResult.contact.RelatedContactId}
          previousContactId={props.contactResult.contact.PreviousContactId}
          selectedLogType={LogType.INTERACTION_LOG}
          onLogTypeChange={() => {}}
          onClose={props.onClose}
        />
        <div className='flex-1'>
          <FlowCanvas contactResult={props.contactResult} contactId={props.contactResult.contact.Id as string} />
        </div>
      </div>
    </LogNavigationProvider>
  );
}
